import { useState, useEffect, useMemo, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useDrag } from '@use-gesture/react';
import { ChevronLeft, ChevronRight, ChevronDown, ArrowRight, MapPin, CornerDownRight, ChevronsRight, ChevronsDown, ChevronsUp, X } from 'lucide-react';
import type { Unit, IndustryType } from '@/types';
import { cn } from '@/lib/utils';
import { MODEL_LABELS, MODEL_TYPE_MAPPING } from '@/lib/constants';
import { getLocationTextClass } from '@/lib/locationUtils';
import { BOUNDS_PADDING, isValidLocation } from '@/components/map/mapConfig';

interface ClusterContentProps {
  units: Unit[];
  allUnits?: Unit[]; // All units in the dataset for zoom calculation
  isMobile: boolean;
  onClose: () => void;
  onViewAll?: () => void;
  map: mapboxgl.Map | null;
  filterActions: {
    setSelectedCompanyIds: (ids: string[]) => void;
    setSelectedGroupIds: (ids: string[]) => void;
    setSelectedIndustryTypes: (types: IndustryType[]) => void;
    setSelectedModels: (models: string[]) => void;
    saveFiltersForRestore: () => void;
    restoreSavedFilters: () => void;
    setShouldExpandAll: (value: boolean) => void;
  };
  hasSavedFilters: boolean;
}

export function ClusterContent({
  units,
  allUnits,
  isMobile,
  onClose,
  onViewAll,
  map,
  filterActions,
  hasSavedFilters
}: ClusterContentProps) {
  const { setSelectedCompanyIds, setSelectedGroupIds, setSelectedIndustryTypes, setSelectedModels, saveFiltersForRestore, restoreSavedFilters, setShouldExpandAll } = filterActions;

  // Group units by company
  const companyGroups = useMemo(() => units.reduce((acc, unit) => {
    const companyName = unit.company?.name || 'Unknown';
    if (!acc[companyName]) {
      acc[companyName] = {
        units: [],
        logo: unit.company?.logo_url,
        filmUnitCount: 0 // Track number of film units
      };
    }
    acc[companyName].units.push(unit);
    if (unit.industry_type === 'film') {
      acc[companyName].filmUnitCount++;
    }
    return acc;
  }, {} as Record<string, { units: Unit[]; logo?: string; filmUnitCount: number }>), [units]);

  const [currentCompanyIndex, setCurrentCompanyIndex] = useState(0);
  const companyNames = Object.keys(companyGroups);
  const currentCompany = companyNames[currentCompanyIndex];
  const currentGroup = companyGroups[currentCompany];
  
  // Consider it a film company if it has at least one film unit
  const isFilmCompany = currentGroup.filmUnitCount > 0;

  // Group current company's units by parent group then site
  const sortedGroups = useMemo(() => {
    const groups = currentGroup.units.reduce((acc, unit) => {
      let groupName = 'Unassigned';
      let groupId: string | null = null;
      let siteName = 'Main Unit';
      let siteId: string | null = null;

      if (unit.primary_group) {
        if (unit.primary_group.parent_group) {
          groupName = unit.primary_group.parent_group.name;
          groupId = unit.primary_group.parent_group.id;
          siteName = unit.primary_group.name;
          siteId = unit.primary_group.id;
        } else {
          groupName = unit.primary_group.name;
          groupId = unit.primary_group.id;
          siteName = 'Main Unit';
          siteId = unit.primary_group.id;
        }
      }

      if (!acc[groupName]) {
        acc[groupName] = {
          id: groupId,
          count: 0,
          units: [],
          sites: {} as Record<string, { id: string | null, count: number, units: Unit[] }>
        };
      }

      acc[groupName].count++;
      acc[groupName].units.push(unit);

      if (!acc[groupName].sites[siteName]) {
        acc[groupName].sites[siteName] = { id: siteId, count: 0, units: [] };
      }
      acc[groupName].sites[siteName].count++;
      acc[groupName].sites[siteName].units.push(unit);

      return acc;
    }, {} as Record<string, { id: string | null, count: number, units: Unit[], sites: Record<string, { id: string | null, count: number, units: Unit[] }> }>);

    // Sort groups by unit count (descending)
    return Object.entries(groups).sort((a, b) => b[1].count - a[1].count);
  }, [currentGroup]);

  const totalGroups = sortedGroups.length;

  const handleViewAll = () => {
    if (onViewAll) {
      onViewAll();
      return;
    }

    const currentCompanyId = currentGroup.units[0]?.company_id;
    if (currentCompanyId) {
      // Determine unique industry types in this company
      const uniqueIndustryTypes = Array.from(new Set(currentGroup.units.map(u => u.industry_type).filter(Boolean))) as IndustryType[];

      // Get ALL model codes for these industry types
      const relevantModelCodes = Object.entries(MODEL_TYPE_MAPPING)
        .filter(([_code, types]) => types.some(type => uniqueIndustryTypes.includes(type as IndustryType)))
        .map(([code, _types]) => code);

      // Filter to show only this company's units
      setSelectedCompanyIds([currentCompanyId]);
      setSelectedGroupIds([]); // Clear group filters to show all
      setSelectedIndustryTypes(uniqueIndustryTypes); // Set industry types based on company
      setSelectedModels(relevantModelCodes); // Select ALL models from the company's industries
    }

    // Zoom to fit all units in the cluster (which are from this company in this context)
    if (map && units.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      // Only include units with valid GPS from the current group for the bounds (not 0,0, not missing)
      const unitsWithLocation = currentGroup.units.filter(u => isValidLocation(u.location));
      unitsWithLocation.forEach((unit) => {
        bounds.extend([unit.location.lng, unit.location.lat]);
      });

      // Close the drawer/popup first
      onClose();

      // Use proper padding based on device type and sidebar
      const padding = isMobile ? BOUNDS_PADDING.mobile : BOUNDS_PADDING.desktop;

      if (unitsWithLocation.length > 0) {
        map.fitBounds(bounds, {
          padding,
          duration: 800,
          maxZoom: 15,
          essential: true,
        });
      }
    }
  };

  const handleFilterGroup = (groupId: string | null, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expansion toggle
    const currentCompanyId = currentGroup.units[0]?.company_id;

    // Save current filters before applying new selection (only if not already saved)
    if (!hasSavedFilters) {
      saveFiltersForRestore();
    }

    // Find units for this group
    const groupUnits = currentGroup.units.filter(u => {
        if (!groupId) return !u.primary_group_id; // Unassigned
        if (u.primary_group?.id === groupId) return true;
        if (u.primary_group?.parent_group_id === groupId) return true;
        return false;
    });

    // Determine unique industry types in this group
    const uniqueIndustryTypes = Array.from(new Set(groupUnits.map(u => u.industry_type).filter(Boolean))) as IndustryType[];

    // Get ALL model codes for these industry types (not just the ones in this group)
    // This ensures we show all MODEL units from the same industries when filtering
    const relevantModelCodes = Object.entries(MODEL_TYPE_MAPPING)
      .filter(([_code, types]) => types.some(type => uniqueIndustryTypes.includes(type as IndustryType)))
      .map(([code, _types]) => code);

    if (currentCompanyId) {
      setSelectedCompanyIds([currentCompanyId]);
      
      if (groupId) {
        // Collect group IDs: parent group + all child sites
        const groupIdsToSelect: string[] = [groupId];
        
        // Check if this is a group (has child sites)
        // Find the parent group in sortedGroups that matches this groupId
        const parentGroup = sortedGroups.find(([_name, data]) => data.id === groupId);
        if (parentGroup) {
          // This is a group - also select all child sites
          const [_groupName, groupData] = parentGroup;
          Object.values(groupData.sites).forEach(site => {
            if (site.id && site.id !== groupId) {
              groupIdsToSelect.push(site.id);
            }
          });
        }
        
        setSelectedGroupIds(groupIdsToSelect);
      } else {
        const unassignedId = `__unassigned_${currentCompanyId}__`;
        setSelectedGroupIds([unassignedId]);
      }
      
      // Auto-select industry types based on the group's units
      if (uniqueIndustryTypes.length > 0) {
        setSelectedIndustryTypes(uniqueIndustryTypes);
      }
      // Auto-select ALL models from the group's industries to show MODEL units
      setSelectedModels(relevantModelCodes);
    }

    // Trigger expand-all so filtered hierarchy is fully visible in sidebar (matches sidebar pinpoint behavior)
    setShouldExpandAll(true);

    // Calculate zoom bounds to include ONLY units in the selected group
    // This matches the sidebar pinpoint behavior - zoom to what's filtered
    if (map) {
        const unitsToZoom = allUnits || currentGroup.units;

        // Filter units that match the selected group only (not MODEL units from other groups)
        const matchingUnits = unitsToZoom.filter(unit => {
          // Must match the selected company
          if (unit.company_id !== currentCompanyId) return false;

          // Must match the selected group (consistent with sidebar pinpoint behavior)
          const matchesGroup = groupId
            ? (unit.primary_group?.id === groupId || unit.primary_group?.parent_group_id === groupId)
            : !unit.primary_group_id;

          return matchesGroup;
        });

        if (matchingUnits.length > 0) {
          // Filter to units with valid GPS coordinates (not 0,0, not missing)
          const unitsWithLocation = matchingUnits.filter(u => isValidLocation(u.location));

          onClose();

          if (unitsWithLocation.length > 0) {
            const bounds = new mapboxgl.LngLatBounds();
            unitsWithLocation.forEach((unit) => {
              bounds.extend([unit.location.lng, unit.location.lat]);
            });

            // Use proper padding based on device type and sidebar
            const padding = isMobile ? BOUNDS_PADDING.mobile : BOUNDS_PADDING.desktop;

            map.fitBounds(bounds, {
              padding,
              duration: 800,
              maxZoom: 15,
              essential: true,
            });
          }
        } else {
          onClose();
        }
    } else {
        onClose();
    }
  };

  const handleClearSelection = () => {
    // Restore previous filters using global restoration
    restoreSavedFilters();
    onClose();
  };

  const categoryLabel = isFilmCompany
    ? (totalGroups === 1 ? 'Production' : 'Productions')
    : (totalGroups === 1 ? 'Group' : 'Groups');

  // State for expanded groups and sites
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [expandedSites, setExpandedSites] = useState<Record<string, boolean>>({});

  // Auto-expand single items when switching companies or on mount
  const lastCompanyIndex = useRef(-1);

  useEffect(() => {
    if (currentCompanyIndex !== lastCompanyIndex.current) {
      lastCompanyIndex.current = currentCompanyIndex;

      const newExpandedProds: Record<string, boolean> = {};
      const newExpandedSubs: Record<string, boolean> = {};

      // If only one parent group, expand it by default
      if (sortedGroups.length === 1) {
        const [prodName] = sortedGroups[0];
        newExpandedProds[prodName] = true;
      }

      // Check each group for single sites
      sortedGroups.forEach(([prodName, prodData]) => {
        const sites = Object.keys(prodData.sites);
        if (sites.length === 1) {
          const uniqueKey = `${prodName}-${sites[0]}`;
          newExpandedSubs[uniqueKey] = true;
        }
      });

      setExpandedGroups(newExpandedProds);
      setExpandedSites(newExpandedSubs);
    }
  }, [currentCompanyIndex, sortedGroups]);

  const toggleGroup = (name: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const toggleSite = (name: string) => {
    setExpandedSites(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  // Check if all groups and sites are collapsed
  const allCollapsed = useMemo(() => {
    const anyProductionExpanded = sortedGroups.some(([prodName]) => expandedGroups[prodName]);
    const anySiteExpanded = Object.values(expandedSites).some(v => v);
    return !anyProductionExpanded && !anySiteExpanded;
  }, [expandedGroups, expandedSites, sortedGroups]);

  // Toggle all groups and sites
  const toggleAll = () => {
    if (allCollapsed) {
      // Expand all
      const newExpandedProds: Record<string, boolean> = {};
      const newExpandedSubs: Record<string, boolean> = {};

      sortedGroups.forEach(([prodName, prodData]) => {
        newExpandedProds[prodName] = true;
        Object.keys(prodData.sites).forEach(siteName => {
          const uniqueKey = `${prodName}-${siteName}`;
          newExpandedSubs[uniqueKey] = true;
        });
      });

      setExpandedGroups(newExpandedProds);
      setExpandedSites(newExpandedSubs);
    } else {
      // Collapse all
      setExpandedGroups({});
      setExpandedSites({});
    }
  };

  // Horizontal swipe to change companies
  const bind = useDrag(({ swipe: [swipeX] }) => {
    if (swipeX < 0) {
      // Swipe Left -> Next Company
      setCurrentCompanyIndex((prev) => Math.min(companyNames.length - 1, prev + 1));
    } else if (swipeX > 0) {
      // Swipe Right -> Previous Company
      setCurrentCompanyIndex((prev) => Math.max(0, prev - 1));
    }
  }, {
    axis: 'x',
    filterTaps: true,
  });

  return (
    <div 
      {...bind()} 
      className="flex flex-col h-full touch-pan-y"
    >
      {/* Company Header Area */}
      <div className="shrink-0 border-b border-border/40 bg-card/30">
        <div className="px-4 py-4">
          <div className="flex items-center gap-3.5">
            {currentGroup.logo && (
              <div className="w-11 h-11 rounded-lg border border-border/60 overflow-hidden flex-shrink-0 shadow-sm bg-background">
                <img src={currentGroup.logo} alt={currentCompany} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex flex-col min-w-0 gap-0.5">
              <span className="text-base font-bold text-foreground line-clamp-1 leading-tight tracking-tight">{currentCompany}</span>
              
              <div className="flex items-center gap-2 text-muted-foreground/80">
                <div className="flex items-center gap-1 min-w-0">
                  <MapPin className="w-3 h-3 shrink-0" />
                  <span className={cn('text-xs truncate', getLocationTextClass(currentGroup.units[0]?.locationSource))}>{currentGroup.units[0]?.location.address || 'Unknown location'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Groups List */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium flex items-center justify-between w-full px-4 py-2 bg-muted/50 border-b border-border/50">
          <span>{categoryLabel}</span>
          <button
            onClick={toggleAll}
            className="flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-medium text-[#8FB560] hover:text-[#7fa050] hover:bg-muted/50 rounded transition-colors uppercase tracking-wide"
            aria-label={allCollapsed ? "Expand all groups" : "Collapse all groups"}
          >
            {allCollapsed ? (
              <>
                <ChevronsDown className="w-3 h-3" />
                <span>Expand All</span>
              </>
            ) : (
              <>
                <ChevronsUp className="w-3 h-3" />
                <span>Collapse All</span>
              </>
            )}
          </button>
        </div>
        <div className="dark:bg-muted/30 overflow-y-auto custom-scrollbar flex-1">
          {sortedGroups.map(([groupName, groupData]) => (
            <div key={groupName} className="border-b border-border/40 last:border-0">
                {/* Group Header - Clickable to toggle expansion */}
                <div className="flex items-stretch group">
                  <button 
                      onClick={() => toggleGroup(groupName)}
                      className="flex-1 flex items-center justify-between pl-4 pr-3 py-3 text-sm font-medium text-foreground hover:bg-muted/30 transition-colors min-w-0 text-left"
                  >
                      <div className="flex items-center gap-3 min-w-0">
                          <div className="w-5 flex justify-center shrink-0">
                            {expandedGroups[groupName] ? (
                              <ChevronDown className="h-4 w-4 text-[#8FB560]" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-[#8FB560]" />
                            )}
                          </div>
                          <span className="truncate leading-tight">{groupName}</span>
                      </div>
                      <div className="shrink-0 pl-2">
                        <span className="text-xs font-medium text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full tabular-nums border border-border/50">
                          {groupData.count}
                        </span>
                      </div>
                  </button>
                  
                  {/* Filter Button for Production - Full Height Green Button */}
                  <button
                    onClick={(e) => handleFilterGroup(groupData.id, e)}
                    className="w-12 flex items-center justify-center bg-[#8FB560] hover:bg-[#7fa050] text-black transition-colors shrink-0"
                    aria-label={`Filter by ${groupName}`}
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </button>
                </div>

                {/* Sites - Only show if expanded */}
                {expandedGroups[groupName] && (
                    <div className="animate-in slide-in-from-top-1 duration-200 bg-muted/10">
                    {Object.entries(groupData.sites)
                        .sort((a, b) => b[1].count - a[1].count)
                            .map(([siteName, siteData]) => {
                              const uniqueKey = `${groupName}-${siteName}`;
                              const isExpanded = expandedSites[uniqueKey];

                              return (
                                <div key={uniqueKey} className="border-t border-border/30 first:border-0">
                                  <div className="flex items-stretch">
                                    <button
                                        className="flex-1 flex items-center justify-between pl-4 pr-2 py-2.5 hover:bg-muted/30 transition-colors text-xs group/sub text-left min-w-0"
                                        onClick={() => toggleSite(uniqueKey)}
                                    >
                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                          <div className="w-5 flex justify-center shrink-0">
                                            <CornerDownRight className="h-3.5 w-3.5 text-[#8FB560]/70" />
                                          </div>
                                          <span className="truncate text-muted-foreground group-hover/sub:text-foreground transition-colors font-medium">
                                            {siteName}
                                          </span>
                                          {isExpanded ? (
                                            <ChevronDown className="h-3 w-3 text-[#8FB560] shrink-0" />
                                          ) : (
                                            <ChevronRight className="h-3 w-3 text-[#8FB560] shrink-0" />
                                          )}
                                        </div>
                                        <div className="shrink-0 pl-2">
                                          <span className="text-[10px] font-medium text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded-full tabular-nums border border-border/50">
                                            {siteData.count}
                                          </span>
                                        </div>
                                    </button>

                                    {/* Filter Button for Site */}
                                    <button
                                      onClick={(e) => handleFilterGroup(siteData.id, e)}
                                      className="w-10 flex items-center justify-center text-muted-foreground hover:text-[#8FB560] hover:bg-muted/50 transition-colors shrink-0 border-l border-border/30"
                                      aria-label={`Filter by ${siteName}`}
                                    >
                                      <ChevronsRight className="h-3.5 w-3.5" />
                                    </button>
                                  </div>

                                  {/* Model Inventory List - Restored to show Model counts */}
                                  {isExpanded && (
                                    <div className="animate-in slide-in-from-top-1 duration-200 bg-background/50 border-t border-border/30">
                                      {(() => {
                                        // Group units by model code (first 3 chars)
                                        const modelCounts = siteData.units.reduce((acc, unit) => {
                                          const modelCode = unit.unit_code.substring(0, 3);
                                          acc[modelCode] = (acc[modelCode] || 0) + 1;
                                          return acc;
                                        }, {} as Record<string, number>);

                                        // Sort by count descending
                                        return (
                                          <div className="flex justify-center w-full py-2">
                                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 w-[calc(85%+32px)] max-w-[312px]">
                                              {Object.entries(modelCounts)
                                              .sort((a, b) => b[1] - a[1])
                                              .map(([modelCode, count]) => (
                                                <div key={modelCode} className="flex items-center justify-between py-1 text-xs min-w-0 gap-1">
                                                  <span className="truncate text-muted-foreground" title={`${MODEL_LABELS[modelCode] || modelCode} (${modelCode})`}>
                                                    {MODEL_LABELS[modelCode] || modelCode} <span className="opacity-70 text-[10px]">({modelCode})</span>
                                                  </span>
                                                  <span className="font-mono font-medium tabular-nums text-foreground bg-muted/50 px-1.5 rounded text-[10px]">{count}</span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                    </div>
                )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer Actions - Pagination and View All */}
      <div className="shrink-0 mt-auto border-t border-border/50 bg-muted/5 pb-0">
        <div className="flex items-stretch h-12 divide-x divide-border/50">
          {companyNames.length > 1 ? (
            <>
              {/* Pagination */}
              <div className="flex-1 flex items-center justify-between min-w-0">
                <button
                  onClick={() => setCurrentCompanyIndex((prev) => Math.max(0, prev - 1))}
                  disabled={currentCompanyIndex === 0}
                  className="h-full px-4 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:hover:text-muted-foreground transition-colors hover:bg-muted/30"
                  aria-label="Previous company"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <span className="text-xs font-medium text-foreground/80 tabular-nums truncate px-2">
                  Company {currentCompanyIndex + 1} <span className="text-muted-foreground">/</span> {companyNames.length}
                </span>

                <button
                  onClick={() => setCurrentCompanyIndex((prev) => Math.min(companyNames.length - 1, prev + 1))}
                  disabled={currentCompanyIndex === companyNames.length - 1}
                  className="h-full px-4 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:hover:text-muted-foreground transition-colors hover:bg-muted/30"
                  aria-label="Next company"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* View All or Clear Selection */}
              {hasSavedFilters ? (
                <button
                  onClick={handleClearSelection}
                  className="px-6 flex items-center justify-center gap-2 bg-destructive/5 hover:bg-destructive/10 text-destructive font-medium transition-colors text-xs uppercase tracking-wider whitespace-nowrap"
                >
                  <X className="w-3.5 h-3.5" />
                  <span>Clear Selection</span>
                </button>
              ) : (
                <button
                  onClick={handleViewAll}
                  className="px-6 flex items-center justify-center gap-2 bg-primary/5 hover:bg-primary/10 text-primary font-medium transition-colors text-xs uppercase tracking-wider whitespace-nowrap"
                >
                  <span>View All</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </>
          ) : (
            /* View All or Clear Selection Full Width */
            hasSavedFilters ? (
              <button
                onClick={handleClearSelection}
                className="w-full flex items-center justify-center gap-2 text-destructive hover:bg-destructive/5 font-medium transition-colors h-full text-sm"
              >
                <X className="w-4 h-4" />
                <span>Clear Selection</span>
              </button>
            ) : (
              <button
                onClick={handleViewAll}
                className="w-full flex items-center justify-center gap-2 text-primary hover:bg-primary/5 font-medium transition-colors h-full text-sm"
              >
                <span>View All Units</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

